import React, { useState, useEffect, useRef } from 'react';
import {
  ButtonGroup,
  Button,
  Typography,
  useTheme,
  Box,
  Grid,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  StopCircle as StopCircleIcon,
  Pause as PauseIcon,
  PlayCircleFilled as PlayIcon,
  Notes as NotesIcon,
  Mic as MicIcon,
} from '@mui/icons-material';
import { AudioWaveform as AudioWaveformIcon } from 'lucide-mui';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import meetingImage from './assets/meeting.webp';

const AudioRecorderDemo: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [status, setStatus] = useState<'ready' | 'recording' | 'paused'>(
    'ready'
  );
  const [recordingTime, setRecordingTime] = useState(0);
  const [dataArray, setDataArray] = useState<Uint8Array>(new Uint8Array(128)); // Same as real AudioWaveform (fftSize 256 / 2 = 128 frequencyBinCount)
  const [previousDataArray, setPreviousDataArray] = useState<Uint8Array>(
    new Uint8Array(128)
  ); // For smoothing between frames
  const [demoStep, setDemoStep] = useState(0);
  const [updateCounter, setUpdateCounter] = useState(0); // For slower updates
  const [showPreview, setShowPreview] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const animationRef = useRef<number | null>(null);

  const isRecording = status === 'recording';
  const isPaused = status === 'paused';
  const showTimer = isRecording || isPaused;
  const showControls = isRecording || isPaused;
  const waveformColor = theme.palette.secondary.main;

  // Simulate realistic frequency data like the real AudioWaveform
  const generateSimulatedFrequencyData = () => {
    const bufferLength = 128; // Same as real analyser.frequencyBinCount with fftSize 256
    const tempDataArray = new Uint8Array(bufferLength);
    const now = Date.now();

    // Create simple, realistic speech patterns
    const timeInSeconds = now / 1000;

    // Simple speech volume envelope - much calmer
    const speechCycle = Math.sin(timeInSeconds * 0.3) * 0.3 + 0.7; // Slower, smaller variation
    const volumeEnvelope = Math.max(0.4, speechCycle); // Higher minimum, less dramatic

    // Simple breathing pattern
    const breathingPattern = Math.sin(timeInSeconds * 0.2) * 0.1 + 0.9; // Very subtle
    const overallVolume = volumeEnvelope * breathingPattern;

    // Much simpler frequency content
    for (let i = 0; i < bufferLength; i += 1) {
      const frequency = (i / bufferLength) * 22050; // Map to frequency range (0-22kHz)

      let amplitude = 0;

      // Simplified speech model
      if (frequency < 300) {
        // Low-end fundamentals - stable
        amplitude = 60 + Math.sin(timeInSeconds * 1.5 + i * 0.1) * 15;
      } else if (frequency < 1500) {
        // Main speech range - most energy here
        amplitude = 80 + Math.sin(timeInSeconds * 2 + i * 0.05) * 20;
      } else if (frequency < 4000) {
        // Clarity range - moderate energy
        amplitude = 40 + Math.sin(timeInSeconds * 1.2 + i * 0.03) * 15;
      } else if (frequency < 8000) {
        // High frequencies - less energy
        amplitude = 20 + Math.sin(timeInSeconds * 0.8 + i * 0.02) * 10;
      } else {
        // Very high frequencies - minimal energy
        amplitude = 5 + Math.sin(timeInSeconds * 0.5 + i * 0.01) * 5;
      }

      // Apply overall volume envelope
      amplitude *= overallVolume;

      // Ensure value is in valid range
      const finalValue = Math.max(5, Math.min(120, amplitude)); // Much lower max amplitude
      tempDataArray[i] = Math.round(finalValue);
    }

    // Simple smoothing pass
    for (let i = 1; i < bufferLength - 1; i += 1) {
      const smoothed =
        (tempDataArray[i - 1] + tempDataArray[i] * 2 + tempDataArray[i + 1]) /
        4;
      tempDataArray[i] = Math.round(smoothed);
    }

    return tempDataArray;
  };

  // Update waveform data when recording (slower updates)
  useEffect(() => {
    if (isRecording && !isPaused) {
      const draw = () => {
        setUpdateCounter((prev) => prev + 1);

        // Only update every 3rd frame to slow down animation
        if (updateCounter % 3 === 0) {
          const newData = generateSimulatedFrequencyData();

          // Smooth transition between current and previous data
          const smoothedData = new Uint8Array(128);
          for (let i = 0; i < 128; i += 1) {
            // Blend 70% new data with 30% previous data for smoother transitions
            smoothedData[i] = Math.round(
              newData[i] * 0.7 + previousDataArray[i] * 0.3
            );
          }

          setPreviousDataArray(new Uint8Array(dataArray)); // Store current as previous
          setDataArray(smoothedData);
        }

        animationRef.current = requestAnimationFrame(draw);
      };
      draw();
    } else if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isRecording, isPaused, updateCounter, dataArray, previousDataArray]);

  // Timer for recording
  useEffect(() => {
    if (isRecording && !isPaused) {
      intervalRef.current = window.setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRecording, isPaused]);

  // Demo animation sequence
  useEffect(() => {
    const timer = setTimeout(
      () => {
        switch (demoStep) {
          case 0:
            setStatus('recording');
            setRecordingTime(0);
            // Show preview 500ms after recording starts
            setTimeout(() => setShowPreview(true), 500);
            setDemoStep(1);
            break;
          case 1:
            setStatus('paused');
            setDemoStep(2);
            break;
          case 2:
            setStatus('recording');
            setDemoStep(3);
            break;
          case 3:
            setStatus('recording');
            setDemoStep(4);
            break;
          case 4:
            setStatus('ready');
            setRecordingTime(0);
            setShowPreview(false);
            setDemoStep(0);
            break;
          default:
            break;
        }
      },
      demoStep === 0 ? 500 : 3000
    );

    return () => clearTimeout(timer);
  }, [demoStep]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`;
  };

  const getOverlayContent = () => {
    if (isPaused) {
      return (
        <Typography
          sx={{
            color: theme.palette.text.primary,
            fontSize: '1rem',
          }}
        >
          {t('library.tour.audioRecorder.status.paused')}
        </Typography>
      );
    }

    if (status === 'ready') {
      return (
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
              transform: 'scale(1.05)',
              '& .MuiSvgIcon-root': {
                color: 'white',
              },
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          <MicIcon
            sx={{
              fontSize: 40,
              color: 'white !important',
              '&.MuiSvgIcon-root': {
                color: 'white !important',
              },
            }}
          />
        </Box>
      );
    }

    return null;
  };

  const togglePauseResume = () => {
    setStatus(isPaused ? 'recording' : 'paused');
  };

  const stopRecording = () => {
    setStatus('ready');
    setRecordingTime(0);
  };

  // Exact copy of AudioWaveform component rendering logic
  const AudioWaveform = () => {
    const BAR_WIDTH = 4;
    const BAR_GAP = 1; // Reduced gap to allow bars to extend further
    const SVG_HEIGHT = 120;
    const halfBufferLength = dataArray.length / 2;
    const SVG_WIDTH = halfBufferLength * (BAR_WIDTH + BAR_GAP) * 2;

    // Normalize dataArray to avoid saturation in the middle
    const maxVal = Math.max(...dataArray, 1); // Prevent division by zero
    const normalizedDataArray = dataArray.map(
      (value) => (value / maxVal) * 255
    );

    const leftBars = Array.from(
      normalizedDataArray.slice(0, halfBufferLength)
    ).reverse();
    const rightBars = Array.from(
      normalizedDataArray.slice(0, halfBufferLength)
    );

    return (
      <div style={{ width: '100%', height: '100%' }}>
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          preserveAspectRatio="none" // This ensures the SVG stretches to fill container
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {leftBars.map((value, index) => (
            <React.Fragment key={`left-${index}`}>
              <rect
                x={index * (BAR_WIDTH + BAR_GAP)}
                y={SVG_HEIGHT / 2 - (value / 255) * (SVG_HEIGHT / 2)}
                width={BAR_WIDTH}
                height={(value / 255) * (SVG_HEIGHT / 2)}
                fill={waveformColor}
              />
              <rect
                x={index * (BAR_WIDTH + BAR_GAP)}
                y={SVG_HEIGHT / 2}
                width={BAR_WIDTH}
                height={(value / 255) * (SVG_HEIGHT / 2)}
                fill={waveformColor}
              />
            </React.Fragment>
          ))}
          {rightBars.map((value, index) => (
            <React.Fragment key={`right-${index}`}>
              <rect
                x={SVG_WIDTH / 2 + index * (BAR_WIDTH + BAR_GAP)}
                y={SVG_HEIGHT / 2 - (value / 255) * (SVG_HEIGHT / 2)}
                width={BAR_WIDTH}
                height={(value / 255) * (SVG_HEIGHT / 2)}
                fill={waveformColor}
              />
              <rect
                x={SVG_WIDTH / 2 + index * (BAR_WIDTH + BAR_GAP)}
                y={SVG_HEIGHT / 2}
                width={BAR_WIDTH}
                height={(value / 255) * (SVG_HEIGHT / 2)}
                fill={waveformColor}
              />
            </React.Fragment>
          ))}
        </svg>
      </div>
    );
  };

  const ControlButtons = () => {
    const iconColor =
      theme.palette.mode === 'dark'
        ? theme.palette.secondary.light
        : theme.palette.secondary.contrastText;

    const buttonSize = '3rem';
    const iconSize = '2rem';
    const stopIconSize = '2.5rem';

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          gap: '0.5rem',
        }}
      >
        <Tooltip title={t('library.tour.audioRecorder.controls.notes')} arrow>
          <IconButton
            sx={{
              width: buttonSize,
              height: buttonSize,
              padding: '0.25rem',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                transform: 'translateY(-2px) scale(1.05)',
              },
              '&:active': {
                transform: 'translateY(0) scale(0.95)',
              },
            }}
          >
            <NotesIcon
              sx={{
                fontSize: iconSize,
                color: iconColor,
                transition: 'all 0.2s ease-in-out',
              }}
            />
          </IconButton>
        </Tooltip>

        <Tooltip
          title={t(
            isPaused
              ? 'library.tour.audioRecorder.controls.resume'
              : 'library.tour.audioRecorder.controls.pause'
          )}
          arrow
        >
          <IconButton
            color="secondary"
            onClick={togglePauseResume}
            sx={{
              width: buttonSize,
              height: buttonSize,
              padding: '0.25rem',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                transform: 'translateY(-2px) scale(1.05)',
              },
              '&:active': {
                transform: 'translateY(0) scale(0.95)',
              },
            }}
          >
            {isPaused ? (
              <PlayIcon
                sx={{
                  fontSize: iconSize,
                  color: iconColor,
                  transition: 'all 0.2s ease-in-out',
                }}
              />
            ) : (
              <PauseIcon
                sx={{
                  fontSize: iconSize,
                  color: iconColor,
                  transition: 'all 0.2s ease-in-out',
                }}
              />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title={t('library.tour.audioRecorder.controls.stop')} arrow>
          <IconButton
            color="secondary"
            onClick={stopRecording}
            sx={{
              width: buttonSize,
              height: buttonSize,
              padding: '0.25rem',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                transform: 'translateY(-2px) scale(1.05)',
              },
              '&:active': {
                transform: 'translateY(0) scale(0.95)',
              },
              '&:hover .MuiSvgIcon-root': {
                color: theme.palette.error.main,
              },
            }}
          >
            <StopCircleIcon
              sx={{
                fontSize: stopIconSize,
                color: theme.palette.error.main,
                transition: 'all 0.2s ease-in-out',
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const ScreenRecordingPreview = () => {
    return (
      <Box
        sx={{
          width: '100%',
          height: 200,
          backgroundColor: theme.palette.grey[300],
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          mb: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={meetingImage}
          alt={t('library.tour.audioRecorder.preview.alt')}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: theme.palette.error.main,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 },
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{ fontSize: '0.75rem', fontWeight: 500 }}
          >
            {t('library.tour.audioRecorder.status.recording')}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <AudioWaveformIcon sx={{ fontSize: 20 }} />
          {t('library.tour.audioRecorder.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('library.tour.audioRecorder.description')}
        </Typography>
      </Box>

      {/* Screen Recording Preview */}
      <Collapse in={showPreview} timeout={600}>
        <ScreenRecordingPreview />
      </Collapse>

      {/* Recorder Interface - Exact copy of RecorderControls styling */}
      <Grid
        container
        direction="column"
        sx={{
          bgcolor: theme.palette.background.paper,
          p: 1,
          mb: 2,
        }}
      >
        <Grid
          item
          container
          alignItems="center"
          justifyContent="center"
          sx={{ height: '3.75rem' }}
        >
          <Grid item sx={{ flex: 1 }}>
            <ButtonGroup
              sx={{
                height: '3.75rem',
                width: '100%',
              }}
            >
              <Button
                color="secondary"
                variant="text"
                sx={{
                  width: '100%',
                  height: '100%',
                  bgcolor: theme.palette.background.paper,
                  '&:hover': {
                    bgcolor: theme.palette.background.paper,
                  },
                }}
              >
                <Box
                  sx={{ position: 'relative', height: '100%', width: '100%' }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: '100%',
                      opacity: isRecording ? 1 : 0,
                    }}
                  >
                    <AudioWaveform />
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: alpha(
                        theme.palette.background.paper,
                        0.8
                      ),
                      opacity: isPaused || status === 'ready' ? 1 : 0,
                    }}
                  >
                    {getOverlayContent()}
                  </Box>
                </Box>
              </Button>
            </ButtonGroup>
          </Grid>

          <Grid
            item
            sx={{
              opacity: status === 'ready' ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out',
              visibility: status === 'ready' ? 'hidden' : 'visible',
            }}
          >
            {showTimer && (
              <Typography
                sx={{
                  color: theme.palette.secondary.contrastText,
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  fontSize: '1rem',
                }}
              >
                {formatTime(recordingTime)}
              </Typography>
            )}
          </Grid>

          <Grid
            item
            sx={{
              opacity: status === 'ready' ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out',
              visibility: status === 'ready' ? 'hidden' : 'visible',
            }}
          >
            {showControls && <ControlButtons />}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AudioRecorderDemo;
